import { VisibilityStatus } from '../../../types/download';
import { HistoricalReportData, ReportData } from '../../../types/reportData';
import { TableColumnType } from '../../../types/universalTracker';
import { formatCalculationNumber } from '../../../utils/formula';
import { DataSource, getValueListName, shouldDisplayPrivateText } from '../common';
import { getLatestNote } from '../group-builder';

export const defaultPrivateQuestionText = 'Company has marked this as Private';

export const CHART_DEFAULTS = {
  OPTIONS: {
    colors: ['#A6CFE5', '#D6F5DF', '#1A5A87'],
    pieSliceTextStyle: {
      color: '#0c3866',
    },
    legend: {
      position: 'top'
    },
    chartArea: {
      top: 40,
      bottom: 20,
      left: 20,
      right: 20,
    },
    titleTextStyle: {
      color: 'a1a3a4'
    }
  }
};

export interface GenerateChartsProps {
  updateData?: (key: string, data: DataSource) => void;
  questionData: HistoricalReportData[];
  visibility: VisibilityStatus;
}

export interface TotalEmissionIntensityType {
  code: string,
  name: string,
  intensity: number | string | undefined,
  numericValue: number;
}

/**
 * This just select last note, no reason to look at history?
 * @link getLatestNote
 */
export const getComments = (reportData: ReportData[], utrCode: string, visibility: VisibilityStatus, emptyText: string = 'No further explanation provided.') => {
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);

  if (!utrv) {
    return emptyText;
  }

  if (shouldDisplayPrivateText(utrv, visibility)) {
    return defaultPrivateQuestionText;
  }

  return getLatestNote(utrv, emptyText)
}

const EMISSION_INTENSITY_DEFAULT_DATA = {
  code: '',
  name: '',
  intensity: '',
  numericValue: 0,
};

/** Way too complicated... **/
export const getEmissionIntensityData = (
  reportData: ReportData[],
  utrCode: string,
  currentTotals: TotalEmissionIntensityType[],
  visibility: VisibilityStatus
): TotalEmissionIntensityType => {
  const utrv = reportData.find((d) => d.universalTracker.code === utrCode);

  if (!utrv || !utrv.valueData?.table) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  if (shouldDisplayPrivateText(utrv, visibility)) {
    return {
      code: '',
      name: defaultPrivateQuestionText,
      intensity: defaultPrivateQuestionText,
      numericValue: 0,
    }
  }

  const [firstRow] = utrv.valueData.table;
  if (!firstRow) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  /**
   * Available column codes.
   * Denominator is a list of other column codes.
   * [
   *     "sgx56-total-emissions",
   *     "sgx56-denominator", // Determine which columns to show
   *
   *     "sgx56-rev",
   *     "sgx56-emissions-intens-rev", // sgx56-denominator1
   *
   *     "sgx56-headcount",
   *     "sgx56-emissions-intens-headcount", // sgx56-denominator2
   *
   *     "sgx56-floorarea", // sgx56-denominator3
   *     "sgx56-emissions-intens-floorarea",
   *
   *     "sgx56-other-org-metric", // sgx56-denominator4
   *     "sgx56-other-org-metric-amount",
   *     "sgx56-emissions-intens-othermetric"
   * ]
   */
  const initialCode = currentTotals.find((item) => item.code)?.code ?? '';
  const columns = utrv.universalTracker.valueValidation?.table?.columns;

  const table = utrv.valueData.table;
  const sgx56Denominator = 'sgx56-denominator';

  // Find a row that have the same code as previous one, or if we don't have one select first
  // as we have not yet determined our search column value like all questions by revenue or headcount etc.
  // @TODO: Technically there could be more rows with the same denominator, since we don't have unique validation.
  const tableRow = initialCode ? table.find((rows) => {
    return rows.find((column) => column.code === sgx56Denominator)?.value === initialCode;
  }): firstRow;

  // utrv table does not contain a row with the code we need, skip it
  if (!tableRow) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  // On the row for the right denominator, should have only one intensity calculated column
  const valueColumn = tableRow.find((col) => /emissions-intens/.test(col.code) && col.value !== undefined);
  if (!valueColumn) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  const tableColumn = columns?.find((c) => c.code === valueColumn.code);
  if (!tableColumn) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  // Find the name for our denominator value
  const code = tableRow.find((col) => col.code === sgx56Denominator)?.value as string;
  const name = code === 'sgx56-denominator4'
    ? (tableRow.find((col) => col.code === 'sgx56-other-org-metric')?.value as string)
    : getValueListName(utrv, code);

  const value = valueColumn.value;
  if (typeof value === 'string' && tableColumn.type === TableColumnType.Number) {
    try {
      return {
        code,
        intensity: formatCalculationNumber(value),
        name: name ?? '',
        numericValue: parseFloat(value)
      };
    } catch (e) {
      console.error(e)
    }
  }

  if (typeof value === 'number') {
    return {
      code,
      intensity: formatCalculationNumber(value),
      name: name ?? '',
      numericValue: value
    };
  }

  return { code, intensity: '', name: name ?? '', numericValue: 0 };
};
